import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, Check, X, Menu, X as XIcon, Target, TrendingUp, Shield, Clock, BarChart3, Phone } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'Ansatz', href: '#ansatz' },
    { label: 'Vorteile', href: '#vorteile' },
    { label: 'Kriterien', href: '#kriterien' },
    { label: 'FAQ', href: '#faq' },
  ];

  const faqs = [
    { q: 'Was kostet das Erstgespräch?', a: 'Das Erstgespräch ist komplett kostenfrei und unverbindlich. Wir nutzen 30 Minuten, um Ihr Geschäftsmodell zu verstehen und einzuschätzen, ob eine Zusammenarbeit für beide Seiten sinnvoll ist.' },
    { q: 'Wie funktioniert das erfolgsorientierte Modell?', a: 'Sie zahlen einen transparenten Fixpreis pro Kampagne. Die Vergütung erfolgt pro qualifiziertem Termin, der tatsächlich stattfindet. Kein Termin, keine Kosten.' },
    { q: 'Wann kann ich mit den ersten Ergebnissen rechnen?', a: 'In der Regel starten die ersten qualifizierten Gespräche innerhalb von 10-14 Tagen nach Projektstart. Die genaue Timeline hängt von Ihrer Branche und Zielgruppe ab.' },
    { q: 'Für welche Branchen funktioniert das?', a: 'Wir arbeiten branchenübergreifend mit B2B-Unternehmen — von Software über Beratung bis hin zu spezialisierten Dienstleistern. Entscheidend ist ein klarer Zielkunde und ein Angebot mit ausreichend Marge.' },
    { q: 'Was passiert bei einem No-Show?', a: 'Termine, die nicht wahrgenommen werden, werden nicht berechnet. Wir tragen das Risiko gemeinsam und sind incentiviert, nur hochwertige Gespräche zu vereinbaren.' },
    { q: 'Wie qualifiziert ihr die Termine?', a: 'Wir definieren vorab gemeinsam klare Kriterien — Entscheider-Level, Budget, konkreter Bedarf. Nur Kontakte, die diese Kriterien erfüllen, werden als Termin gewertet.' },
  ];

  return (
    <div className="bg-white text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="landing-page">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200' : 'bg-transparent'}`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <span className="font-black text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>VALORYX</span>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{l.label}</a>
            ))}
          </div>
          <a
            href="/quiz"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white text-sm font-bold rounded-md hover:opacity-90 transition-colors"
          >
            Potenzialanalyse <ArrowRight className="w-4 h-4" />
          </a>
          <button className="md:hidden text-slate-900" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <XIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-slate-200 px-6 pb-4 space-y-3">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-slate-600">{l.label}</a>
            ))}
            <a href="/quiz" className="block w-full text-center px-5 py-2.5 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white text-sm font-bold rounded-md">Potenzialanalyse starten</a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#2E7CF6]/20 bg-[#2E7CF6]/5 mb-6">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4]" />
              <span className="text-xs font-bold text-[#1B5FD4] tracking-wide uppercase">Pipeline-Wachstum seit 2015</span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.05] mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Qualifizierte Neukundentermine.
              <br />
              <span className="text-[#2E7CF6]">Erfolgsorientiert.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-8">
              Wir generieren planbar Erstgespräche mit kaufbereiten Entscheidern aus Ihrer Zielgruppe. Transparenter Fixpreis, erfolgsorientiert — Sie zahlen nur für Termine, die stattfinden.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/quiz"
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white font-bold rounded-md hover:opacity-90 transition-all hover:shadow-lg"
              >
                Jetzt Potenzialanalyse starten <ArrowRight className="w-5 h-5" />
              </a>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2E7CF6]" /> Unverbindlich</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2E7CF6]" /> In 90 Sekunden</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="hidden md:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#2E7CF6]/5 rounded-3xl -z-10" />
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=500&fit=crop"
                alt="Business strategy meeting"
                className="rounded-2xl w-full object-cover shadow-sm border border-slate-200"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-3 gap-8 text-center">
          <div>
            <p className="text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>1.000+</p>
            <p className="text-sm text-slate-500 mt-1 font-medium">Termine vereinbart</p>
          </div>
          <div>
            <p className="text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>50+</p>
            <p className="text-sm text-slate-500 mt-1 font-medium">Zufriedene Partner</p>
          </div>
          <div>
            <p className="text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>10+</p>
            <p className="text-sm text-slate-500 mt-1 font-medium">Jahre Marketing & Sales</p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="ansatz" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Warum traditionelle Akquise Sie bremst
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Viele Unternehmen verlieren Zeit und Budget an Methoden, die keinen messbaren ROI liefern.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Pauschalen ohne Ergebnis', desc: 'Klassische Agenturen kassieren monatliche Gebühren — unabhängig davon, ob Sie Neukunden gewinnen oder nicht.' },
              { icon: Target, title: 'Leads ohne Kaufabsicht', desc: 'Viele Kampagnen optimieren auf Menge statt Qualität. Das Ergebnis: Ihr Vertrieb verschwendet Zeit mit Kontakten ohne Budget.' },
              { icon: BarChart3, title: 'Fehlende Transparenz', desc: 'Statt konkreter Abschlusszahlen erhalten Sie Reports mit Vanity-Metriken — Klicks und Impressions, aber keine Neukunden.' },
            ].map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.1 } } }}
                className="border border-slate-200 rounded-xl p-8 hover:border-[#2E7CF6]/20 transition-colors"
              >
                <item.icon className="w-8 h-8 text-[#2E7CF6] mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              So läuft die Zusammenarbeit ab
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Drei klare Schritte — von der Analyse bis zum qualifizierten Termin.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'Analyse & Strategie', desc: 'Wir verstehen Ihr Angebot, definieren Ihren Wunschkunden und entwickeln eine maßgeschneiderte Kampagnenstrategie.' },
              { num: '02', title: 'Kampagnen & Outreach', desc: 'Innerhalb von 10-14 Tagen starten gezielte Akquise-Maßnahmen. Wir kombinieren Performance Marketing mit persönlicher Ansprache.' },
              { num: '03', title: 'Qualifizierte Termine', desc: 'Sie erhalten vorqualifizierte Gespräche mit Entscheidern, die konkreten Bedarf haben. Kein Cold-Calling, keine Zeitverschwendung.' },
            ].map((step, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.15 } } }}
                className="relative"
              >
                <span className="text-7xl font-black text-slate-200 leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.num}</span>
                <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-14 text-center">
            <a href="/quiz" data-testid="process-cta" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white font-bold rounded-md hover:opacity-90 transition-all">
              Kostenlose Potenzialanalyse starten <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section id="vorteile" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Ihre Vorteile mit VALORYX
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: TrendingUp, title: 'Planbares Wachstum', desc: 'Ein konstanter Strom qualifizierter Gespräche — unabhängig von Empfehlungen oder Zufall.' },
              { icon: Shield, title: 'Minimales Risiko', desc: 'Transparenter Fixpreis, erfolgsorientiert. Sie zahlen primär für Ergebnisse — kein Termin, keine Kosten.' },
              { icon: Clock, title: 'Sofortige Entlastung', desc: 'Ihr Team konzentriert sich auf den Abschluss — die Akquise übernehmen wir komplett.' },
              { icon: Target, title: 'Vorqualifizierte Entscheider', desc: 'Nur Gespräche mit Kontakten, die Budget, Bedarf und Entscheidungskompetenz mitbringen.' },
            ].map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.1 } } }}
                className="flex gap-5 border border-slate-200 rounded-xl p-6 hover:border-[#2E7CF6]/20 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-[#2E7CF6]/5 border border-[#2E7CF6]/15 flex items-center justify-center shrink-0">
                  <item.icon className="w-6 h-6 text-[#2E7CF6]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section id="kriterien" className="py-24 md:py-32 px-6 md:px-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Passt eine Zusammenarbeit?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Unser Modell funktioniert am besten, wenn bestimmte Grundvoraussetzungen erfüllt sind.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
            {[
              { yes: true, text: 'Ihr Angebot hat einen nachgewiesenen Product-Market-Fit' },
              { yes: true, text: 'Mindestauftragswert ab 2.000 EUR' },
              { yes: true, text: 'Sie oder Ihr Team können Termine in Abschluesse verwandeln' },
              { yes: true, text: 'Sie suchen planbares Wachstum, kein Experiment' },
              { yes: false, text: 'Startup ohne Kunden oder Proof-of-Concept' },
              { yes: false, text: 'Keine Bereitschaft, selbst Vertriebsgespraeche zu fuehren' },
            ].map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.05 } } }}
                className={`flex items-center gap-3 px-5 py-4 rounded-lg border ${item.yes ? 'border-[#2E7CF6]/20 bg-[#2E7CF6]/5' : 'border-slate-200 bg-white'}`}
              >
                {item.yes ? (
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                    <X className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                )}
                <span className={`text-sm ${item.yes ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Häufig gestellte Fragen
            </h2>
          </motion.div>
          <div className="space-y-0 border-t border-slate-200">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-slate-200" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left"
                >
                  <span className="text-base font-semibold text-slate-900 pr-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <p className="pb-5 text-sm text-slate-600 leading-relaxed -mt-1">{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4]" data-testid="final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Bereit für qualifizierte Termine?
          </h2>
          <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl mx-auto">
            Starten Sie die kostenlose Potenzialanalyse und erfahren Sie, wie wir Ihren Vertrieb skalieren können.
          </p>
          <a
            href="/quiz"
            data-testid="final-cta-button"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1B5FD4] font-bold rounded-md hover:bg-[#2E7CF6]/5 transition-all"
          >
            Potenzialanalyse starten <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-8">
            <span className="text-white font-black text-lg tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>VALORYX</span>
            <span className="text-slate-500 text-sm">2026 Alle Rechte vorbehalten.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutzerklärung</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
